import React, { useState } from "react";
import { Alert, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import LabeledField from "../../components/form/LabeledField";

interface WorkflowPifReenterSsnCardProps {
  /** Sends the 9 digits to the applicant record (decision P4). Resolves true once it is saved. */
  onSave: (ssn: string) => Promise<boolean>;
  saving: boolean;
  /** The last save was refused for good (403/404/422) — retrying here would not help. */
  refused: boolean;
}

const SSN_DIGITS = 9;

/**
 * A new hire's case that has no applicant record — the SSN was lost to a refresh after a failed save
 * (Pooja, 2026-10-08). Asks for it again through the same P4 step. The input is masked, kept only in
 * this card's state, and cleared once sent; it never becomes a case answer.
 */
const WorkflowPifReenterSsnCard: React.FC<WorkflowPifReenterSsnCardProps> = ({ onSave, saving, refused }) => {
  const [ssn, setSsn] = useState("");
  const digits = ssn.replace(/\D/g, "");

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSsn(event.target.value.replace(/[^\d-]/g, "").slice(0, 11));
  };

  const handleSave = async () => {
    if (digits.length !== SSN_DIGITS || saving) return;
    if (await onSave(digits)) setSsn("");
  };

  return (
    <Alert severity="warning" className="workflow-run__alert" role="region" aria-label="Re-enter the applicant's SSN">
      <Typography component="h3" className="workflow-run__section-title">
        Re-enter the applicant&apos;s SSN
      </Typography>
      <Typography>
        This new hire&apos;s SSN isn&apos;t on their applicant record yet, so the employee can&apos;t be created once
        the PIF is approved. Enter it again to send this PIF for approval.
      </Typography>
      <Box className="workflow-run__lookup">
        <LabeledField controlId="pif-reenter-ssn" label="SSN" required>
          <TextField
            id="pif-reenter-ssn"
            size="small"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={ssn}
            onChange={handleChange}
            placeholder="•••-••-••••"
          />
        </LabeledField>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={digits.length !== SSN_DIGITS || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : undefined}
        >
          {saving ? "Saving…" : "Save SSN"}
        </Button>
      </Box>
      {refused && (
        <Typography role="alert">The SSN couldn&apos;t be saved to this case. Please contact HR.</Typography>
      )}
    </Alert>
  );
};

export default WorkflowPifReenterSsnCard;
